<main>
  <loading-overlay [isLoading]="isLoading"></loading-overlay>

  <!-- Header -->
  <header>
    <div class="headline">
      <h1>
        {{
          originalTransfer()
            ? ('Lot_Item_Transfer_Details_Title' | translateWF: 'Lot Item Transfer Details') + ': ' + originalTransfer()!.number
            : ('Lot_Item_Transfer_Details_New' | translateWF: 'New Lot Item Transfer')
        }}
      </h1>
      @if (originalTransfer()) {
        <csl-dropdown [enumObject]="statusEnumObject" [enumValueOptions]="roleStatusOptions" [formControl]="status"></csl-dropdown>
      }
    </div>
    <div class="buttons">
      <p-button label="{{ 'Lot_Item_Transfer_Details_Cancel' | translateWF: 'Cancel' }}" (click)="cancel()" variant="outlined" />
    </div>
  </header>

  <div class="content">
    <!-- Existing Transfer Mode -->

    @if (originalTransfer()) {
      <!-- Transfer Info -->
      <p-panel [header]="'Transfer_Details' | translateWF: 'Transfer Details'">
        <p-card>
          <div class="grid-12">
            <div class="span-12-3">
              <span class="text-gray-500">{{ 'Transfer_Type' | translateWF: 'Transfer Type' }}</span>
              <div class="mt-1">
                <p-tag [value]="getTransferTypeLabel(originalTransfer()!.transferType)" [severity]="getTransferTypeSeverity(originalTransfer()!.transferType)">
                  <ng-template pTemplate="icon">
                    <i [class]="getTransferTypeIcon(originalTransfer()!.transferType)"></i>
                  </ng-template>
                </p-tag>
              </div>
            </div>
            <div class="span-12-4">
              <span class="text-gray-500">{{ 'From_Location' | translateWF: 'From Location' }}</span>
              <p class="font-semibold">{{ originalTransfer()!.fromLocationName || originalTransfer()!.fromLocationCode }}</p>
            </div>
            <div class="span-12-4">
              <span class="text-gray-500">{{ 'To_Location' | translateWF: 'To Location' }}</span>
              <p class="font-semibold">{{ originalTransfer()!.toLocationName || originalTransfer()!.toLocationCode }}</p>
            </div>
          </div>
        </p-card>
      </p-panel>

      <!-- Status History (show only if 2+ entries) -->
      @if (originalTransfer()!.statusHistory && originalTransfer()!.statusHistory.length >= 2) {
        <p-panel [header]="'Status_History' | translateWF: 'Status History'">
          <p-card>
            <p-timeline [value]="originalTransfer()!.statusHistory" layout="horizontal" align="top">
              <ng-template #content let-event>
                @let statusHistoryStrict = getStatusHistoryStrict(event);
                <div>
                  <p-tag [value]="getStatusLabel(statusHistoryStrict.status)" />
                  <p class="text-xs text-gray-500 mt-1 mb-0">{{ statusHistoryStrict.date | date: 'dd/MM/yy HH:mm' }}</p>
                  @if (statusHistoryStrict.user && statusHistoryStrict.user.username) {
                    <p class="text-xs text-gray-400 m-0">{{ statusHistoryStrict.user.username }}</p>
                  }
                </div>
              </ng-template>
              <ng-template #opposite let-event><span>&nbsp;</span></ng-template>
            </p-timeline>
          </p-card>
        </p-panel>
      }

      <!-- Notes Section using csl-standard-notes -->
      <p-panel [header]="'Notes' | translateWF: 'Notes'">
        <ng-template pTemplate="header">
          <div class="row-start-center gap-1">
            @if (isSampleTransfer()) {
              <span class="text-red-500 text-lg">*</span>
            }
          </div>
        </ng-template>
        <csl-standard-notes [userLite]="userLite!" [notes]="originalTransfer()!.notes || []" [viewOnly]="false" (noteAdded)="onNoteAdded($event)"></csl-standard-notes>
        @if (isSampleTransfer() && !hasNotes()) {
          <div class="text-red-500 text-sm mt-2">
            {{
              'Sample_Notes_Required'
                | translateWF
                  : 'Es obligatorio indicar la razón de la muestra antes de avanzar la
        transferencia.'
            }}
          </div>
        }
      </p-panel>

      <!-- Added Items Table -->
      <p-panel [header]="'Added_Items' | translateWF: 'Added Items'">
        <ng-template pTemplate="icons">
          <p-tag [value]="originalTransfer()!.items.length.toString()" severity="success" />
        </ng-template>
        <p-card data-variant="compact">
          @if (isLotItemBasedTransfer()) {
            <!-- Lot-item-based transfers (RM ↔ FG, Chemical Batch → FG, RM relocation): Lot Items table -->
            @if (addedLotItems().length > 0) {
              <p-table [value]="addedLotItems()">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ 'No' | translateWF: 'No.' }}</th>
                    <th>{{ 'Lot_Number' | translateWF: 'Lot Number' }}</th>
                    <th>{{ 'Item_Code' | translateWF: 'Item Code' }}</th>
                    <th>{{ 'Description' | translateWF: 'Description' }}</th>
                    <th>{{ 'Provider' | translateWF: 'Provider' }}</th>
                    <th>{{ 'Manufacturer' | translateWF: 'Manufacturer' }}</th>
                    <th>{{ 'Mfr_Lot_No' | translateWF: 'Mfr. Lot No.' }}</th>
                    <th>{{ 'Quantity' | translateWF: 'Quantity' }}</th>
                    @if (!readonly()) {
                      <th>{{ 'Actions' | translateWF: 'Actions' }}</th>
                    }
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-item let-i="rowIndex">
                  @let lotItemStrict = getLotItemEntryStrict(item);
                  <tr>
                    <td>{{ i + 1 }}</td>
                    <td>{{ lotItemStrict.number }}</td>
                    <td>{{ lotItemStrict.itemCode || '-' }}</td>
                    <td>{{ (lotItemStrict.itemDisplayName | localized) || '-' }}</td>
                    <td>{{ lotItemStrict.provider?.name || '-' }}</td>
                    <td>{{ lotItemStrict.manufacturer?.name || '-' }}</td>
                    <td>{{ lotItemStrict.manufacturerLotNumber || '-' }}</td>
                    <td>{{ (lotItemStrict.currentQuantity | number: '1.2-2') + ' ' + getLotItemUomLabel(lotItemStrict) }}</td>
                    @if (!readonly()) {
                      <td>
                        <p-button
                          icon="pi pi-minus"
                          [label]="'Remove' | translateWF: 'Remove'"
                          [text]="true"
                          severity="danger"
                          (onClick)="isRawMaterialRelocation() ? removeLotItemForRelocation(lotItemStrict) : removeLotItem(lotItemStrict)"
                          [disabled]="isLoading"
                        />
                      </td>
                    }
                  </tr>
                </ng-template>
              </p-table>
            } @else {
              <p class="text-gray-500 text-center py-4">
                {{ 'No_Lot_Items_Added_Yet' | translateWF: 'No lot items added yet. Search and add items below.' }}
              </p>
            }
          } @else {
            <!-- Standard/Relocation: Rolls table -->
            @if (addedRolls().length > 0) {
              <p-table [value]="addedRolls()">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ 'No' | translateWF: 'No.' }}</th>
                    <th>{{ 'Roll_Number' | translateWF: 'Roll Number' }}</th>
                    <th>{{ 'Fabric_Code' | translateWF: 'Fabric Code' }}</th>
                    <th>{{ 'Description' | translateWF: 'Description' }}</th>
                    <th>{{ 'Production_Card' | translateWF: 'Production Card' }}</th>
                    <th>{{ 'Work_Order' | translateWF: 'Work Order' }}</th>
                    <th>{{ 'Color' | translateWF: 'Color' }}</th>
                    <th>{{ 'Quality' | translateWF: 'Quality' }}</th>
                    <th>{{ 'Category' | translateWF: 'Category' }}</th>
                    <th>{{ useStationWeight() ? ('Station_Weight_Lb' | translateWF: 'Station Wt (lb)') : ('Weight_Lb' | translateWF: 'Weight (lb)') }}</th>
                    <th>{{ useStationWeight() ? ('Station_Weight_Kg' | translateWF: 'Station Wt (kg)') : ('Weight_Kg' | translateWF: 'Weight (kg)') }}</th>
                    @if (!readonly()) {
                      <th>{{ 'Actions' | translateWF: 'Actions' }}</th>
                    }
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-item let-i="rowIndex">
                  <tr>
                    <td>{{ i + 1 }}</td>
                    <td>{{ item.number }}</td>
                    <td>{{ item?.expectedFabricCode || '-' }}</td>
                    <td>{{ item?.expectedFabricDescription || '-' }}</td>
                    <td>{{ item?.productionCard?.number || '-' }}</td>
                    <td>{{ item?.workOrder?.number || '-' }}</td>
                    <td>{{ item?.colorName || '-' }}</td>
                    <td>
                      <p-tag [value]="getQualityLabel(item?.quality)" [severity]="getQualitySeverity(item?.quality)">
                        <ng-template pTemplate="icon"><i [class]="getQualityIcon(item?.quality)"></i></ng-template>
                      </p-tag>
                    </td>
                    <td>
                      <p-tag [value]="getCategoryLabel(item?.category)" [severity]="getCategorySeverity(item?.category)" />
                    </td>
                    <td>{{ getRollWeight(item) | number: '1.2-2' }}</td>
                    <td>{{ lbsToKg(getRollWeight(item)) | number: '1.2-2' }}</td>
                    @if (!readonly()) {
                      <td>
                        <p-button
                          icon="pi pi-minus"
                          [label]="'Remove' | translateWF: 'Remove'"
                          [text]="true"
                          severity="danger"
                          (onClick)="removeItem(item)"
                          [disabled]="isLoading"
                        />
                      </td>
                    }
                  </tr>
                </ng-template>
                <ng-template pTemplate="footer">
                  <tr class="font-bold">
                    <td colspan="9" class="text-right">{{ 'Total' | translateWF: 'Total' }}:</td>
                    <td>{{ totalWeightLbs() | number: '1.2-2' }}</td>
                    <td>{{ totalWeightKg() | number: '1.2-2' }}</td>
                    @if (!readonly()) {
                      <td></td>
                    }
                  </tr>
                </ng-template>
              </p-table>
            } @else {
              <p class="text-gray-500 text-center py-4">
                {{ 'No_Items_Added_Yet' | translateWF: 'No items added yet. Search and add rolls below.' }}
              </p>
            }
          }
        </p-card>
      </p-panel>

      <!-- Available Items Section (Draft mode only) -->
      @if (originalTransfer()!.status === LotItemTransferStatus.Draft) {
        <p-panel [header]="isLotItemBasedTransfer() ? ('Add_Lot_Items' | translateWF: 'Add Lot Items') : ('Add_Items' | translateWF: 'Add Items')">
          <ng-template pTemplate="icons">
            @if (isLotItemBasedTransfer()) {
              <p-tag [value]="(isRawMaterialRelocation() ? rmRelocationTotalRecords() : lotItemTotalRecords()).toString()" />
            } @else {
              <p-tag [value]="totalRecords().toString()" />
            }
          </ng-template>

          <div class="col-xs">
            <!-- Location Pickers for Relocation (Finished Goods and Raw Material) -->
            @if (isRelocationTransfer()) {
              <p-card>
                <div class="grid-auto-3">
                  <div class="form-box">
                    <label class="block text-gray-600">{{ 'Source_Location' | translateWF: 'Source Location' }} <span class="text-red-500">*</span></label>
                    <p-treeSelect
                      fluid
                      class="w-full"
                      [options]="sourceLocationTree()"
                      [(ngModel)]="selectedSourceLocation"
                      placeholder="{{ 'Select_Source_Location' | translateWF: 'Select source location...' }}"
                      selectionMode="single"
                      (onNodeSelect)="onSourceLocationSelect($event)"
                      [disabled]="isRawMaterialRelocation() ? addedLotItems().length > 0 : addedRolls().length > 0"
                    />
                  </div>
                  <div class="form-box">
                    <label class="block text-gray-600">{{ 'Target_Location' | translateWF: 'Target Location' }}</label>
                    <p-treeSelect
                      fluid
                      class="w-full"
                      [options]="targetLocationTree()"
                      [(ngModel)]="selectedTargetLocation"
                      placeholder="{{ 'Select_Target_Location' | translateWF: 'Select target location...' }}"
                      [showClear]="true"
                      selectionMode="single"
                      (onNodeSelect)="onTargetLocationSelect($event)"
                      [disabled]="isRawMaterialRelocation() ? addedLotItems().length > 0 : addedRolls().length > 0"
                    />
                  </div>
                </div>
                @if ((isRawMaterialRelocation() ? addedLotItems().length : addedRolls().length) > 0) {
                  <div class="span-12">
                    <p-message severity="warn" styleClass="w-full">
                      {{ 'Remove_Items_To_Change' | translateWF: 'Remove all items to change location' }}
                    </p-message>
                  </div>
                }
              </p-card>
            }

            @if (isLotItemBasedTransfer()) {
              @if (isRawMaterialRelocation()) {
                <!-- Raw Material Relocation: available lot items from the dedicated endpoint -->
                <!-- Search Filters -->
                <p-panel header="Search Filters" data-tone="surface">
                  <ng-template pTemplate="icons">
                    <p-button
                      icon="pi pi-refresh"
                      [rounded]="true"
                      [text]="true"
                      (onClick)="searchAvailableLotItemsForRelocation()"
                      [disabled]="isLoading"
                      pTooltip="{{ 'Refresh' | translateWF: 'Refresh' }}"
                    />
                    <p-button
                      icon="pi pi-filter-slash"
                      [rounded]="true"
                      [text]="true"
                      (onClick)="clearRmRelocationFilters()"
                      [disabled]="isLoading"
                      pTooltip="{{ 'Clear_Filters' | translateWF: 'Clear Filters' }}"
                    />
                  </ng-template>
                  <p-card>
                    <div class="grid-auto-3">
                      <div class="form-box">
                        <label class="block text-gray-600 mb-1">{{ 'Lot_Number' | translateWF: 'Lot Number' }}</label>
                        <input
                          type="text"
                          pInputText
                          class="w-full"
                          [ngModel]="rmRelocationLotNumberFilter()"
                          (ngModelChange)="rmRelocationLotNumberFilter.set($event); onRmRelocationFilterChange()"
                          placeholder="{{ 'Search_Lot_Number' | translateWF: 'Search by lot number...' }}"
                        />
                      </div>
                      <div class="form-box">
                        <label class="block text-gray-600 mb-1">{{ 'Item_Code' | translateWF: 'Item Code' }}</label>
                        <input
                          type="text"
                          pInputText
                          class="w-full"
                          [ngModel]="rmRelocationItemCodeFilter()"
                          (ngModelChange)="rmRelocationItemCodeFilter.set($event); onRmRelocationFilterChange()"
                          placeholder="{{ 'Search_Item_Code' | translateWF: 'Search by item code...' }}"
                        />
                      </div>
                      <div class="form-box">
                        <label class="block text-gray-600 mb-1">{{ 'Provider' | translateWF: 'Provider' }}</label>
                        <input
                          type="text"
                          pInputText
                          class="w-full"
                          [ngModel]="rmRelocationProviderFilter()"
                          (ngModelChange)="rmRelocationProviderFilter.set($event); onRmRelocationFilterChange()"
                          placeholder="{{ 'Search_Provider' | translateWF: 'Search by provider...' }}"
                        />
                      </div>
                      <div class="form-box">
                        <label class="block text-gray-600 mb-1">{{ 'Manufacturer' | translateWF: 'Manufacturer' }}</label>
                        <input
                          type="text"
                          pInputText
                          class="w-full"
                          [ngModel]="rmRelocationManufacturerFilter()"
                          (ngModelChange)="rmRelocationManufacturerFilter.set($event); onRmRelocationFilterChange()"
                          placeholder="{{ 'Search_Manufacturer' | translateWF: 'Search by manufacturer...' }}"
                        />
                      </div>
                      <div class="form-box">
                        <label class="block text-gray-600 mb-1">{{ 'Mfr_Lot_No' | translateWF: 'Mfr. Lot No.' }}</label>
                        <input
                          type="text"
                          pInputText
                          class="w-full"
                          [ngModel]="rmRelocationMfrLotFilter()"
                          (ngModelChange)="rmRelocationMfrLotFilter.set($event); onRmRelocationFilterChange()"
                          placeholder="{{ 'Search_Mfr_Lot' | translateWF: 'Search by mfr. lot no...' }}"
                        />
                      </div>
                    </div>
                  </p-card>
                </p-panel>

                <p-card data-variant="compact">
                  @if (rmRelocationResults().length > 0) {
                    <p-table
                      [value]="rmRelocationResults()"
                      [lazy]="true"
                      [paginator]="true"
                      [rows]="pageSize()"
                      [totalRecords]="rmRelocationTotalRecords()"
                      [rowsPerPageOptions]="[10, 25, 50]"
                      (onLazyLoad)="onRmRelocationPageChange($event)"
                    >
                      <ng-template pTemplate="header">
                        <tr>
                          <th>{{ 'Lot_Number' | translateWF: 'Lot Number' }}</th>
                          <th>{{ 'Item_Code' | translateWF: 'Item Code' }}</th>
                          <th>{{ 'Provider' | translateWF: 'Provider' }}</th>
                          <th>{{ 'Manufacturer' | translateWF: 'Manufacturer' }}</th>
                          <th>{{ 'Mfr_Lot_No' | translateWF: 'Mfr. Lot No.' }}</th>
                          <th>{{ 'Quantity' | translateWF: 'Quantity' }}</th>
                          <th>{{ 'Actions' | translateWF: 'Actions' }}</th>
                        </tr>
                      </ng-template>
                      <ng-template pTemplate="body" let-item>
                        <tr>
                          <td>{{ item.number }}</td>
                          <td>{{ item.itemCode || '-' }}</td>
                          <td>{{ item.providerName || '-' }}</td>
                          <td>{{ item.manufacturerName || '-' }}</td>
                          <td>{{ item.manufacturerLotNumber || '-' }}</td>
                          <td>{{ item.netQuantity | number: '1.2-2' }}</td>
                          <td>
                            <p-button
                              icon="pi pi-plus"
                              [label]="'Add' | translateWF: 'Add'"
                              [text]="true"
                              severity="success"
                              (onClick)="addAvailableLotItemForRelocation(item)"
                              [disabled]="isLoading"
                            />
                          </td>
                        </tr>
                      </ng-template>
                    </p-table>
                  } @else {
                    <p class="text-gray-500 text-center py-4">
                      {{ 'No_Lot_Items_Found_RM' | translateWF: 'No lot items found at the selected source location.' }}
                    </p>
                  }
                </p-card>
              } @else {
                <!-- RM ↔ FG and Chemical Batch → FG: lot item search section -->
                <!-- Search Filters -->
                <p-panel header="Search Filters" data-tone="surface">
                  <ng-template pTemplate="icons">
                    <p-button
                      icon="pi pi-refresh"
                      [rounded]="true"
                      [text]="true"
                      (onClick)="searchLotItems()"
                      [disabled]="isLoading"
                      pTooltip="{{ 'Refresh' | translateWF: 'Refresh' }}"
                    />
                    <p-button
                      icon="pi pi-filter-slash"
                      [rounded]="true"
                      [text]="true"
                      (onClick)="clearLotItemFilters()"
                      [disabled]="isLoading"
                      pTooltip="{{ 'Clear_Filters' | translateWF: 'Clear Filters' }}"
                    />
                  </ng-template>
                  <p-card>
                    <div class="grid-auto-3">
                      <div class="form-box">
                        <label class="block text-gray-600 mb-1">{{ 'Lot_Number' | translateWF: 'Lot Number' }}</label>
                        <input
                          type="text"
                          pInputText
                          class="w-full"
                          [ngModel]="lotItemSearchTerm()"
                          (ngModelChange)="onLotItemSearchTermChange($event)"
                          placeholder="{{ 'Search_Lot_Item' | translateWF: 'Search by lot number...' }}"
                        />
                      </div>
                      <div class="form-box">
                        <label class="block text-gray-600 mb-1">{{ 'Item_Code' | translateWF: 'Item Code' }}</label>
                        <input
                          type="text"
                          pInputText
                          class="w-full"
                          [ngModel]="lotItemItemCodeFilter()"
                          (ngModelChange)="lotItemItemCodeFilter.set($event); onLotItemFilterChange()"
                          placeholder="{{ 'Search_Item_Code' | translateWF: 'Search by item code...' }}"
                        />
                      </div>
                      @if (showLotItemManufacturerFilter()) {
                        <div class="form-box">
                          <label class="block text-gray-600 mb-1">{{ 'Manufacturer' | translateWF: 'Manufacturer' }}</label>
                          <p-autoComplete
                            class="w-full"
                            styleClass="w-full"
                            [ngModel]="lotItemManufacturerFilter()"
                            [suggestions]="lotItemManufacturerSuggestions()"
                            (completeMethod)="onLotItemManufacturerSearch($event)"
                            (onSelect)="onLotItemManufacturerChange($event.value)"
                            (onClear)="onLotItemManufacturerChange(null)"
                            optionLabel="name"
                            [forceSelection]="true"
                            [showClear]="true"
                            appendTo="body"
                            placeholder="{{ 'Search_Manufacturer' | translateWF: 'Search by manufacturer...' }}"
                          />
                        </div>
                      }
                      <div class="form-box">
                        <label class="block text-gray-600 mb-1">{{ 'Mfr_Lot_No' | translateWF: 'Mfr. Lot No.' }}</label>
                        <input
                          type="text"
                          pInputText
                          class="w-full"
                          [ngModel]="lotItemManufacturerLotFilter()"
                          (ngModelChange)="lotItemManufacturerLotFilter.set($event); onLotItemFilterChange()"
                          placeholder="{{ 'Search_Mfr_Lot' | translateWF: 'Search by mfr. lot no...' }}"
                        />
                      </div>
                    </div>
                  </p-card>
                </p-panel>

                <p-card data-variant="compact">
                  @if (lotItemSearchResults().length > 0) {
                    <p-table
                      [value]="lotItemSearchResults()"
                      [lazy]="true"
                      [paginator]="true"
                      [rows]="pageSize()"
                      [totalRecords]="lotItemTotalRecords()"
                      [rowsPerPageOptions]="[10, 25, 50]"
                      (onLazyLoad)="onLotItemPageChange($event)"
                    >
                      <ng-template pTemplate="header">
                        <tr>
                          <th>{{ 'Lot_Number' | translateWF: 'Lot Number' }}</th>
                          <th>{{ 'Item_Code' | translateWF: 'Item Code' }}</th>
                          <th>{{ 'Description' | translateWF: 'Description' }}</th>
                          <th>{{ 'Provider' | translateWF: 'Provider' }}</th>
                          <th>{{ 'Manufacturer' | translateWF: 'Manufacturer' }}</th>
                          <th>{{ 'Mfr_Lot_No' | translateWF: 'Mfr. Lot No.' }}</th>
                          <th>{{ 'Quantity' | translateWF: 'Quantity' }}</th>
                          <th>{{ 'Actions' | translateWF: 'Actions' }}</th>
                        </tr>
                      </ng-template>
                      <ng-template pTemplate="body" let-lotItem>
                        <tr>
                          <td>{{ lotItem.number }}</td>
                          <td>{{ lotItem.itemCode || '-' }}</td>
                          <td>{{ lotItem.itemDisplayName?.en || lotItem.itemDisplayName?.es || '-' }}</td>
                          <td>{{ lotItem.provider?.name || '-' }}</td>
                          <td>{{ lotItem.manufacturer?.name || '-' }}</td>
                          <td>{{ lotItem.manufacturerLotNumber || '-' }}</td>
                          <td>{{ (getLotItemQuantity(lotItem) | number: '1.2-2') + ' ' + getLotItemUomLabel(lotItem) }}</td>
                          <td>
                            <p-button
                              icon="pi pi-plus"
                              [label]="'Add' | translateWF: 'Add'"
                              [text]="true"
                              severity="success"
                              (onClick)="addLotItem(lotItem)"
                              [disabled]="isLoading"
                            />
                          </td>
                        </tr>
                      </ng-template>
                    </p-table>
                  } @else {
                    <p class="text-gray-500 text-center py-4">
                      {{ 'No_Lot_Items_Found' | translateWF: 'No lot items found in Raw Materials warehouse.' }}
                    </p>
                  }
                </p-card>
              }
            } @else {
              <!-- Standard/Relocation FG: Roll search section -->

              <!-- Search Filters -->
              <p-panel header="Search Filters" data-tone="surface">
                <ng-template pTemplate="icons">
                  <p-button
                    icon="pi pi-refresh"
                    [rounded]="true"
                    [text]="true"
                    (onClick)="searchRolls()"
                    [disabled]="isLoading"
                    pTooltip="{{ 'Refresh' | translateWF: 'Refresh' }}"
                  />
                  <p-button
                    icon="pi pi-filter-slash"
                    [rounded]="true"
                    [text]="true"
                    (onClick)="clearAllFilters()"
                    [disabled]="isLoading"
                    pTooltip="{{ 'Clear_Filters' | translateWF: 'Clear Filters' }}"
                  />
                  @if (canAddAll()) {
                    <p-button
                      icon="pi pi-plus-circle"
                      [label]="'Add_All_From_PC' | translateWF: 'Add All from PC'"
                      severity="success"
                      [outlined]="true"
                      (onClick)="addAllAvailable()"
                      [disabled]="isLoading"
                      pTooltip="{{ 'Add_All_From_PC_Tooltip' | translateWF: 'Add all available rolls from the selected Production Card' }}"
                    />
                  }
                </ng-template>
                <p-card>
                  <div class="grid-auto-3">
                    <div class="form-box">
                      <label class="block text-gray-600 mb-1">{{ 'Roll_Number' | translateWF: 'Roll Number' }}</label>
                      <input
                        type="text"
                        pInputText
                        class="w-full"
                        [ngModel]="rollSearchTerm()"
                        (ngModelChange)="onRollSearchTermChange($event)"
                        placeholder="{{ 'Search_Roll' | translateWF: 'Search roll...' }}"
                      />
                    </div>
                    <div class="form-box">
                      <label class="block text-gray-600 mb-1">{{ 'Work_Order' | translateWF: 'Work Order' }}</label>
                      <p-autoComplete
                        fluid
                        class="w-full"
                        styleClass="w-full"
                        #workOrderSearchRef="cslServerSearch"
                        [cslServerSearch]="fetchWorkOrders"
                        [suggestions]="workOrderSearchRef.results()"
                        (onSelect)="onWorkOrderSelect($event)"
                        (onClear)="onWorkOrderClear()"
                        optionLabel="orderNumber"
                        [dropdown]="false"
                        [showClear]="true"
                        placeholder="{{ 'Search_Work_Order' | translateWF: 'Search work order...' }}"
                      />
                    </div>
                    @if (showProductionCardFilter()) {
                      <div class="form-box">
                        <label class="block text-gray-600 mb-1">{{ 'Production_Card' | translateWF: 'Production Card' }}</label>
                        <p-autoComplete
                          fluid
                          class="w-full"
                          styleClass="w-full"
                          #productionCardSearchRef="cslServerSearch"
                          [cslServerSearch]="fetchProductionCards"
                          [suggestions]="productionCardSearchRef.results()"
                          (onSelect)="onProductionCardSelect($event)"
                          (onClear)="onProductionCardClear()"
                          optionLabel="productionCardNumber"
                          [dropdown]="false"
                          [showClear]="true"
                          placeholder="{{ 'Search_Production_Card' | translateWF: 'Search production card...' }}"
                        />
                      </div>
                    }
                  </div>
                </p-card>
              </p-panel>

              <p-card data-variant="compact">
                @if (requiresWorkOrderForSearch() && !selectedWorkOrder()) {
                  <p class="text-gray-500 text-center py-4">
                    {{ 'Select_Work_Order_To_Search' | translateWF: 'Please select a work order to search for available rolls.' }}
                  </p>
                } @else if (availableRolls().length > 0) {
                  <p-table
                    [value]="availableRolls()"
                    [lazy]="true"
                    [paginator]="true"
                    [rows]="pageSize()"
                    [totalRecords]="totalRecords()"
                    [rowsPerPageOptions]="[10, 25, 50]"
                    (onLazyLoad)="onPageChange($event)"
                  >
                    <ng-template pTemplate="header">
                      <tr>
                        <th>{{ 'Roll_Number' | translateWF: 'Roll Number' }}</th>
                        <th>{{ 'Fabric_Code' | translateWF: 'Fabric Code' }}</th>
                        <th>{{ 'Description' | translateWF: 'Description' }}</th>
                        <th>{{ 'Production_Card' | translateWF: 'Production Card' }}</th>
                        <th>{{ 'Work_Order' | translateWF: 'Work Order' }}</th>
                        <th>{{ 'Color' | translateWF: 'Color' }}</th>
                        <th>{{ 'Quality' | translateWF: 'Quality' }}</th>
                        <th>{{ 'Category' | translateWF: 'Category' }}</th>
                        <th>{{ useStationWeight() ? ('Station_Weight_Lb' | translateWF: 'Station Wt (lb)') : ('Weight_Lb' | translateWF: 'Weight (lb)') }}</th>
                        <th>{{ useStationWeight() ? ('Station_Weight_Kg' | translateWF: 'Station Wt (kg)') : ('Weight_Kg' | translateWF: 'Weight (kg)') }}</th>
                        <th>{{ 'Actions' | translateWF: 'Actions' }}</th>
                      </tr>
                    </ng-template>
                    <ng-template pTemplate="body" let-roll>
                      <tr>
                        <td>{{ roll.number }}</td>
                        <td>{{ roll.expectedFabricCode || '-' }}</td>
                        <td>{{ roll.expectedFabricDescription || '-' }}</td>
                        <td>{{ roll.productionCard?.number || '-' }}</td>
                        <td>{{ roll.workOrder?.number || '-' }}</td>
                        <td>{{ roll.colorName || '-' }}</td>
                        <td>
                          <p-tag [value]="getQualityLabel(roll.quality)" [severity]="getQualitySeverity(roll.quality)">
                            <ng-template pTemplate="icon"><i [class]="getQualityIcon(roll.quality)"></i></ng-template>
                          </p-tag>
                        </td>
                        <td>
                          <p-tag [value]="getCategoryLabel(roll.category)" [severity]="getCategorySeverity(roll.category)" />
                        </td>
                        <td>{{ getRollWeight(roll) | number: '1.2-2' }}</td>
                        <td>{{ lbsToKg(getRollWeight(roll)) | number: '1.2-2' }}</td>
                        <td>
                          <p-button icon="pi pi-plus" [label]="'Add' | translateWF: 'Add'" [text]="true" severity="success" (onClick)="addItem(roll)" [disabled]="isLoading" />
                        </td>
                      </tr>
                    </ng-template>
                  </p-table>
                } @else {
                  <p class="text-gray-500 text-center py-4">
                    {{
                      totalRecords() === 0
                        ? ('No_Rolls_Found' | translateWF: 'No rolls found in this location.')
                        : ('All_Rolls_Added' | translateWF: 'All available rolls have been added!')
                    }}
                  </p>
                }
              </p-card>
            }
          </div>
        </p-panel>
      }
    }
  </div>
</main>
